import type { ActivityMeasurement } from "../../types/post";

/**
 * Key used by the shared sets editor to decide whether two consecutive
 * measurements belong to the same activity group. Pack activities are keyed
 * on ActivityTypeID + globalCategoryID so two packs with colliding ids stay
 * separate.
 */
export const activityGroupKey = (measurement: ActivityMeasurement): string =>
  measurement.globalCategoryID
    ? `${measurement.ActivityTypeID}_${measurement.globalCategoryID}`
    : measurement.ActivityTypeID;

/**
 * Number of consecutive-activity groups the editor renders for a list.
 * Mirrors the grouping in MasterSessionDetails (consecutive rows with the
 * same key form one group).
 */
export const countActivityGroups = (
  measurements: readonly ActivityMeasurement[],
): number =>
  measurements.reduce((count, measurement, index) => {
    if (index === 0) return 1;
    return activityGroupKey(measurement) !==
      activityGroupKey(measurements[index - 1])
      ? count + 1
      : count;
  }, 0);

/**
 * Collapsed-by-default initialiser (plan 3e, decision 4): every group starts
 * collapsed the first time a list is shown, in every mode that uses the
 * shared editor. Returns the set of group indices to collapse.
 */
export const initialCollapsedGroups = (
  measurements: readonly ActivityMeasurement[],
): Set<number> => {
  const groupCount = countActivityGroups(measurements);
  const collapsed = new Set<number>();
  for (let i = 0; i < groupCount; i++) collapsed.add(i);
  return collapsed;
};

/**
 * Elapsed-time formatter for the live session timer: always `h:mm:ss`
 * (hours unpadded) so sessions over an hour no longer overflow `mm:ss`.
 */
export const formatElapsedTime = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

/**
 * Column width for variable inputs in the shared sets editor. Tuned so that
 * exactly three columns fill a 360–430 px phone: the editor sits in a
 * `px-8` container (64 px) and columns are separated by `gap-1` (2 × 4 px),
 * so (100vw − 72px) / 3. Desktop keeps the fixed 180 px column.
 */
export const VARIABLE_COLUMN_WIDTH_CLASS =
  "w-[calc((100vw-4.5rem)/3)] sm:w-[180px]";
